import { Tooltip } from "@mui/material";
import MotionSemAutorizacao from "core/components/MotionSemAutorizacao.tsx";
import { AnimatePresence, motion } from "framer-motion";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChartsHandler from "../../../../components/Charts/ChartsHandler.tsx";
import LoadingComponent from "../../../../components/LoadingComponent/LoadingComponent.tsx";
import { useProfile } from "../../../../contexts/ProfileContext.tsx";
import { useToast } from "../../../../contexts/ToastContext.tsx";
import IconButton from "../../../../core/components/IconButton.tsx";
import MotionContainer from "../../../../core/components/MotionContainer.tsx";
import Separator from "../../../../core/components/Separator.tsx";
import FiltroData from "../../../../core/data-entry/FiltroData.tsx";
import Layout from "../../../../core/layout/Layout.tsx";
import { isDataDoisMil, primeiroDiaDosMesFormatado, ultimoDiaDosMesFormatado } from "../../../../core/utils/UtilityDates.ts";
import useService from "../../../../hooks/useService.ts";
import { getIndicadorFaturamentoMensal } from "../../../../services/ModuloGerenciador/Dashboard.js";
import { IndicadorFaturamentoMensalI } from "./Indicador.interface.ts";
import "./IndicadorFaturamentoMensal.css";
import { anoAnterior, anoAtual, formatInt, formatMoney, formatPercent, mesAtual } from "./IndicadorUtil.tsx";

enum StateKey {
	LOADING = "loading",
	RESULTS = "results"
}

function IndicadorFaturamentoMensal() {
	const [tabelaAnteriorLabel, setTabelaAnteriorLabel] = useState('');
	const [tabelaAtualLabel, setTabelaAtualLabel] = useState('');
	const [faturamentoComparativoLabel, setFaturamentoComparativoLabel] = useState('');
	const [dadosGrafico1, setDadosGrafico1] = useState<IndicadorFaturamentoMensalI>();
	const [semAutorizacao, setSemAutorizacao] = useState(false);
	const [datasPesquisa, setDatasPesquisa] = useState<{ inicio: string; fim: string }>({
		inicio: primeiroDiaDosMesFormatado,
		fim: ultimoDiaDosMesFormatado,
	});
	const [viewState, setViewState] = useState<StateKey>(StateKey.LOADING);

	const hasBuscadoDados = useRef(false);
	const timeoutsRef = useRef<number[]>([]);

	const scheduleTimeout = useCallback((fn: () => void, delay = 250) => {
		const id = window.setTimeout(fn, delay);
		timeoutsRef.current.push(id);
		return id;
	}, []);

	const clearPendingTimeouts = useCallback(() => {
		timeoutsRef.current.forEach((id) => clearTimeout(id));
		timeoutsRef.current = [];
	}, []);

	const { profile, hasRegra } = useProfile();

	const { showToast } = useToast();

	const [isLoadingDados, buscarIndicadorFaturamentoMensal] = useService(getIndicadorFaturamentoMensal,
		{
			autoStart: false,
			onData: (result) => {
				scheduleTimeout(() => {
					setViewState(StateKey.RESULTS);
					setDadosGrafico1(result.data);
				}, 300);
			},
			onError: (error) => {
				hasBuscadoDados.current = false;
				if (typeof error?.response?.data === "string") {
					showToast(error.response.data, "error");
				} else {
					showToast(
						"Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte.",
						"error"
					);
				}
			},
		},
	)

	const datasValidadas = useCallback(() => {
		const dataInicio = new Date(datasPesquisa.inicio);
		const dataFim = new Date(datasPesquisa.fim);
		const hoje = new Date();

		if (dataInicio > hoje) {
			showToast("A data de início não pode estar no futuro.", "warning");
			return false;
		}

		if (dataInicio > dataFim) {
			showToast("A data de início não pode ser maior que a data de fim.", "warning");
			return false;
		}

		if (isDataDoisMil(dataInicio.toString()) || isDataDoisMil(dataFim.toString()) || dataInicio.getFullYear() < 2000 || dataFim.getFullYear() < 2000) {
			showToast("Ano inválido. Por favor, selecione uma data válida.", "warning");
			return false;
		}

		return true;
	}, [datasPesquisa, showToast]);

	const buscarDadosFiltrados = useCallback(async () => {
		if (!datasValidadas()) {
			return;
		}

		setViewState(StateKey.LOADING);

		const formatter = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" });
		const dataAtual = new Date(datasPesquisa.fim);
		const mesAnoAtual = formatter.format(dataAtual).charAt(0).toUpperCase() + formatter.format(dataAtual).slice(1);
		const dataAnoAnterior = new Date(dataAtual);
		dataAnoAnterior.setFullYear(dataAnoAnterior.getFullYear() - 1);
		const mesAnoAnterior = formatter.format(dataAnoAnterior).charAt(0).toUpperCase() + formatter.format(dataAnoAnterior).slice(1);

		setFaturamentoComparativoLabel(`${mesAnoAnterior} x ${mesAnoAtual}`);
		setTabelaAnteriorLabel(mesAnoAnterior);
		setTabelaAtualLabel(mesAnoAtual);

		buscarIndicadorFaturamentoMensal({
			token: profile!.token,
			dataInicio: datasPesquisa.inicio,
			dataFim: datasPesquisa.fim,
		});
	}, [profile!.token, datasPesquisa, buscarIndicadorFaturamentoMensal, datasValidadas]);

	const buscarDadosInicial = useCallback(async () => {
		setViewState(StateKey.LOADING);
		setFaturamentoComparativoLabel(`${mesAtual}/${anoAnterior} x ${mesAtual}/${anoAtual}`);
		setTabelaAnteriorLabel(`${mesAtual}/${anoAnterior}`);
		setTabelaAtualLabel(`${mesAtual}/${anoAtual}`);

		buscarIndicadorFaturamentoMensal({
			token: profile!.token,
			dataInicio: primeiroDiaDosMesFormatado,
			dataFim: ultimoDiaDosMesFormatado,
		});
	}, [profile!.token, buscarIndicadorFaturamentoMensal]);

	const handleClearSearch = useCallback(() => {
		setDatasPesquisa({ inicio: primeiroDiaDosMesFormatado, fim: ultimoDiaDosMesFormatado });
		buscarDadosInicial();
		clearPendingTimeouts();
	}, [buscarDadosInicial]);

	useEffect(() => {
		if (profile?.token && !hasBuscadoDados.current) {
			setSemAutorizacao(!hasRegra("Indicador Faturamento Mensal"));
			buscarDadosInicial();
			hasBuscadoDados.current = true;
		}
	}, [profile?.token, hasRegra, buscarDadosInicial]);

	useEffect(() => {
		if (isLoadingDados) {
			scheduleTimeout(() => {
				setViewState(StateKey.LOADING);
			}, 300);
		}
	}, [isLoadingDados]);

	const isLoading = useMemo(() => {
		return isLoadingDados || !profile?.token || (!dadosGrafico1 && !hasBuscadoDados.current);
	}, [isLoadingDados, dadosGrafico1, profile?.token]);

	if (semAutorizacao) {
		return <MotionSemAutorizacao />;
	}

	return (
		<Layout title="Indicador Faturamento Mensal">
			<MotionContainer animationKey="IndicadorFaturamentoMensal" scroll>
				<div className="content-container-scrollable">
					<AnimatePresence mode="wait">
						{viewState === StateKey.RESULTS && (
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.15 }}
								className="charts" key="IndicadorFaturamentoMensalContent">
								<Separator size="lg" />
								<div className="filtro-mes-ano">
									<div className="divergencias-sat-data-container">
										<FiltroData
											title="Início"
											value={datasPesquisa.inicio}
											onChange={(newValue) => setDatasPesquisa(prev => ({ ...prev, inicio: newValue }))}
										/>
										{window.innerWidth > 445 && <div className="filtro-data-divider" />}
										<FiltroData
											title="Fim"
											titleFrente={window.innerWidth < 445}
											value={datasPesquisa.fim}
											onChange={(newValue) => setDatasPesquisa(prev => ({ ...prev, fim: newValue }))}
										/>
									</div>

									<div className="limpar-buttons-indicador">
										<Tooltip title="Pesquisar">
											<IconButton
												icon="Search"
												theme="dark"
												size="xl"
												borderColor="none"
												backgroundColor="#FFFFFF"
												hoverColor="#CCC"
												onClick={buscarDadosFiltrados}
											/>
										</Tooltip>
										<Tooltip title={<p>Limpar Filtro</p>}>
											<motion.div
												initial={{ rotate: 0 }}
												whileHover={{ rotate: 90 }}
												transition={{ duration: 0.2, ease: "easeInOut" }}
												className="select-field-chevron"
											>
												<IconButton
													disabled={isLoading}
													icon="X"
													size="xl"
													borderColor="none"
													onClick={handleClearSearch}
													backgroundColor="rgba(0, 0, 0, 0)"
													theme="dark"
												/>
											</motion.div>
										</Tooltip>
									</div>
								</div>
								<Separator size="md" />
								<div>
									<div className="item-cock-scroll-ind">
										<div className="grid-tabelas">
											<div className="tabela-box">
												<table border={1}>
													<thead className="head-cock">
														<tr>
															<th colSpan={5}>Faturamento por cliente</th>
														</tr>
														<tr>
															<th>Cliente</th>
															<th>Faturamento</th>
															<th>% Sobre faturamento</th>
															<th>Qtd. viagens</th>
															<th>Ticket Médio</th>
														</tr>
													</thead>

													<tbody>
														{dadosGrafico1?.tabelaClientes?.map((item: any) => (
															<tr key={item.cliente}>
																<td><strong>{item.cliente}</strong></td>
																<td style={{ textAlign: 'end' }}>{formatMoney(item.faturamento)}</td>
																<td style={{ textAlign: 'end' }}>{formatPercent(item.percentualSobreFaturamento)}</td>
																<td style={{ textAlign: 'end' }}>{formatInt(item.quantidadeViagens)}</td>
																<td style={{ textAlign: 'end' }}>{formatMoney(item.ticketMedio)}</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>

											<div className="tabela-box">
												<table border={1}>
													<thead className="head-cock">
														<tr>
															<th colSpan={5}>Faturamento {faturamentoComparativoLabel}</th>
														</tr>
														<tr>
															<th>Unidade</th>
															<th>{tabelaAnteriorLabel}</th>
															<th>{tabelaAtualLabel}</th>
															<th>Crescimento A/A</th>
															<th>Margem</th>
														</tr>
													</thead>

													<tbody>
														{dadosGrafico1?.tabelaUnidades?.map((item) => (
															<tr key={item.unidade}>
																<td><strong>{item.unidade}</strong></td>
																<td style={{ textAlign: 'end' }}>{formatMoney(item.faturamentoAnoAnterior)}</td>
																<td style={{ textAlign: 'end' }}>{formatMoney(item.faturamentoAnoSelecionado)}</td>
																<td style={{ textAlign: 'end' }}>{formatPercent(item.crescimentoAA * 100)}</td>
																<td style={{ textAlign: 'end' }}>{formatMoney(item.margem)}</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										</div>
									</div>
									<div className="item-cock">
										<div>
											<ChartsHandler
												data={dadosGrafico1}
												type={"bar-stacked"}
												group={true}
												height="auto"
												width={570}
											/>
										</div>
									</div>
									<div className="item-cock-scroll-ind" style={{ marginTop: 12 }}>
										<div className="tabela-box">
											<table border={1} className="table-wide">
												<thead className="head-cock">
													<tr>
														<th></th>
														{dadosGrafico1?.categories?.map((cat) => (
															<th key={cat}>{cat}</th>
														))}
													</tr>
												</thead>

												<tbody>
													{dadosGrafico1?.series?.map((serie) => (
														<tr key={serie.name}>
															<td>
																<strong>{serie.name}</strong>
															</td>

															{serie.data.map((value, index) => (
																<td style={{ textAlign: 'end', marginRight: 10 }} key={index}>
																	{serie.name.includes("%")
																		? formatPercent(value)
																		: formatMoney(value)}
																</td>
															))}
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>
								</div>
							</motion.div>
						)}
						{viewState === StateKey.LOADING && (
							<LoadingComponent key="IndicadorFaturamentoMensalLoading" />
						)}
					</AnimatePresence>
				</div>
			</MotionContainer>
			<Separator size="lg" />
		</Layout>
	);
}

export default memo(IndicadorFaturamentoMensal);
